import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {BSKY_SERVICE} from '#/lib/constants'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'

const BILLS_RQKEY_ROOT = 'account-bills'
const ASSETS_RQKEY_ROOT = 'account-assets'

export const BILLS_RQKEY = (did: string) => [BILLS_RQKEY_ROOT, did]
export const ASSETS_RQKEY = (did: string) => [ASSETS_RQKEY_ROOT, did]

async function bskyFetch<T>(
  path: string,
  accessJwt: string,
  options?: RequestInit,
): Promise<T> {
  const res = await fetch(`${BSKY_SERVICE}/xrpc/${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessJwt}`,
      ...(options?.body ? {'Content-Type': 'application/json'} : {}),
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as {error?: string}).error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

// ── Read: bills balance ───────────────────────────────────────────────────────

export interface AccountBills {
  did: string
  remainingBills: number
  transformedBills?: number
}

interface RawAccountBillsResponse {
  // Wrapped shape
  accountBills?: {
    did?: string
    remainingBills?: number
    transformedBills?: number
  }
  // Flat shape (API may return fields at top level)
  did?: string
  remainingBills?: number
  transformedBills?: number
}

export function useAccountBillsQuery({did}: {did: string}) {
  const agent = useAgent()

  return useQuery({
    queryKey: BILLS_RQKEY(did),
    queryFn: async (): Promise<AccountBills> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')
      const raw = await bskyFetch<RawAccountBillsResponse>(
        `app.bsky.bills.getAccountBills?accounts=${encodeURIComponent(did)}`,
        accessJwt,
      )
      // Handle both wrapped (accountBills) and flat response shapes.
      const b = raw.accountBills ?? raw
      return {
        did: b.did ?? did,
        remainingBills: b.remainingBills ?? 0,
        transformedBills: b.transformedBills,
      }
    },
    staleTime: STALE.MINUTES.ONE,
    // Always refetch on mount so the dashboard shows up-to-date bills on every visit.
    refetchOnMount: 'always',
    enabled: !!did && !!agent.session?.accessJwt,
  })
}

// ── Read: coins / gameplay assets ────────────────────────────────────────────

export interface AccountAssets {
  did: string
  coins: number
  lastCoinClaimDate: string | null
}

// The API returns fields at the top level (flat), not nested under accountAssets.
interface RawAccountAssetsResponse {
  $type?: string
  did?: string
  coins?: number
  lastCoinClaimDate?: string | null
  // Legacy wrapped shape kept for safety
  accountAssets?: {
    did?: string
    coins?: number
    lastCoinClaimDate?: string | null
  }
}

export function useAccountAssetsQuery({did}: {did: string}) {
  const agent = useAgent()

  return useQuery({
    queryKey: ASSETS_RQKEY(did),
    queryFn: async (): Promise<AccountAssets> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')
      const raw = await bskyFetch<RawAccountAssetsResponse>(
        `app.bsky.actor.getAccountAssets?actor=${encodeURIComponent(did)}`,
        accessJwt,
      )
      // Handle both flat (current API) and wrapped (legacy) response shapes.
      const a = raw.accountAssets ?? raw
      return {
        did: a.did ?? did,
        coins: a.coins ?? 0,
        lastCoinClaimDate: a.lastCoinClaimDate ?? null,
      }
    },
    staleTime: STALE.MINUTES.ONE,
    refetchOnMount: 'always',
    enabled: !!did && !!agent.session?.accessJwt,
  })
}

// ── Mutation: claim daily free coins ─────────────────────────────────────────

export interface ClaimDailyFreeResult {
  coins: number
  claimed: boolean
}

export function useClaimDailyFreeMutation() {
  const agent = useAgent()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({did}: {did: string}): Promise<ClaimDailyFreeResult> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')
      return bskyFetch<ClaimDailyFreeResult>(
        'app.bsky.bills.claimDailyFree',
        accessJwt,
        {method: 'POST', body: JSON.stringify({did})},
      )
    },
    onSuccess: (data, {did}) => {
      queryClient.setQueryData(
        ASSETS_RQKEY(did),
        (old: AccountAssets | undefined) => {
          if (!old) return old
          return {...old, coins: data.coins}
        },
      )
    },
  })
}

// ── Mutation: consume 1 bill (open capsule) ───────────────────────────────────

export function useConsumeBillsMutation() {
  const agent = useAgent()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      bills = 1,
      capsulecreator,
      capsuleId,
      idempotencyKey,
    }: {
      bills?: number
      capsulecreator?: string
      capsuleId?: string
      idempotencyKey?: string
    }): Promise<{paid: number; bills: number}> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')
      const raw = await bskyFetch<{
        output?: {paid: number; bills: number}
        paid?: number
        bills?: number
      }>('app.bsky.bills.consumeBills', accessJwt, {
        method: 'POST',
        body: JSON.stringify({input: {bills, capsulecreator, capsuleId, idempotencyKey}}),
      })
      return raw.output ?? {paid: raw.paid ?? 0, bills: raw.bills ?? 0}
    },
    onSuccess: () => {
      const did = agent.session?.did
      if (!did) return
      // Invalidate so the next read gets the authoritative count from the server.
      // Using setQueryData with the response value caused the dashboard to show 0
      // when the API returned a missing/zero bills field in the response body.
      void queryClient.invalidateQueries({queryKey: BILLS_RQKEY(did)})
    },
  })
}

// ── Mutation: add bills (admin / challenge win) ───────────────────────────────

interface RawAddBillsResponse {
  accountBills?: {did?: string; remainingBills?: number}
}

export function useAddAccountBillsMutation() {
  const agent = useAgent()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      did,
      amount,
    }: {
      did: string
      amount: number
    }): Promise<AccountBills> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')
      const raw = await bskyFetch<RawAddBillsResponse>(
        'app.bsky.bills.addAccountBills',
        accessJwt,
        {
          method: 'POST',
          body: JSON.stringify({accountBills: {did, remainingBills: amount}}),
        },
      )
      const b = raw.accountBills ?? {}
      return {did: b.did ?? did, remainingBills: b.remainingBills ?? 0}
    },
    onSuccess: data => {
      queryClient.setQueryData(
        BILLS_RQKEY(data.did),
        (old: AccountBills | undefined) => {
          if (!old) return data
          return {...old, remainingBills: data.remainingBills}
        },
      )
    },
  })
}

// ── Mutation: exchange bills → TND (teacher cashout) ─────────────────────────

export function useExchangeBillsMutation() {
  const agent = useAgent()

  return useMutation({
    mutationFn: async ({bills}: {bills: number}): Promise<void> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')
      await bskyFetch<unknown>('app.bsky.bills.exchangeBills', accessJwt, {
        method: 'POST',
        body: JSON.stringify({input: {bills}}),
      })
    },
  })
}
